import React from 'react';
import { WebsiteDesignProps } from './types';
import { ChevronDown, Search, Menu, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";

const ReptonHeritage: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Repton School";

    return (
        <div className="font-serif text-[#31374D] bg-white min-h-screen selection:bg-[#31374D] selection:text-white">
            {/* Top Bar - "Global Family" */}
            <div className="bg-[#31374D] text-white text-[10px] uppercase tracking-[0.2em] py-2 px-6 flex justify-end gap-6 font-sans">
                <a href="#" className="hover:opacity-70">Parent Portal</a>
                <a href="#" className="hover:opacity-70">Calendar</a>
                <a href="#" className="hover:opacity-70">Sports</a>
                <a href="#" className="hover:opacity-70">Contact</a>
            </div>

            {/* Navigation */}
            <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100 py-4 transition-all">
                <div className="container mx-auto px-6 flex justify-between items-center">
                    {/* Logo Area */}
                    <div className="flex items-center gap-3">
                         <div className="w-12 h-12 bg-[#31374D] text-white flex items-center justify-center font-bold text-xl rounded-sm">R</div>
                         <div className="text-2xl font-bold uppercase tracking-widest leading-none">
                             {schoolName}
                         </div>
                    </div>

                    {/* Desktop Nav */}
                    <div className="hidden lg:flex items-center gap-8 text-sm font-bold uppercase tracking-widest text-[#31374D]">
                        {['About', 'Admissions', 'Academic', 'Pastoral', 'Co-Curricular'].map(item => (
                            <a key={item} href="#" className="relative group py-2">
                                {item}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#31374D] transition-all duration-300 group-hover:w-full"></span>
                            </a>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-4">
                        <Search size={20} className="text-[#31374D] cursor-pointer" />
                        <div className="hidden md:flex items-center gap-2 text-xs font-sans font-bold uppercase tracking-widest border border-[#31374D] px-4 py-3 hover:bg-[#31374D] hover:text-white transition-colors cursor-pointer">
                            <Menu size={16} /> Menu
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section - Video/Image Background */}
            <section className="relative h-screen overflow-hidden">
                <div className="absolute inset-0">
                    <img src="https://images.unsplash.com/photo-1595126838386-3c080e7226fe?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover" alt="Hero" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#31374D]/80 via-transparent to-transparent"></div>
                </div>
                
                <div className="absolute bottom-20 left-0 w-full text-white p-8 md:p-16">
                    <div className="container mx-auto animate-in slide-in-from-bottom-10 fade-in duration-1000">
                        <span className="block text-sm md:text-base font-sans font-bold uppercase tracking-[0.3em] mb-4 border-l-2 border-white pl-4">
                            Welcome to {schoolName}
                        </span>
                        <h1 className="text-5xl md:text-7xl lg:text-8xl font-medium leading-none mb-8 tracking-tight">
                            A Perfectly <br/> <span className="italic font-light">Balanced</span> Education
                        </h1>
                        <div className="flex gap-4">
                             <Button className="bg-white text-[#31374D] hover:bg-gray-100 rounded-none h-14 px-8 uppercase tracking-widest font-sans font-bold text-xs">
                                 Explore Schools
                             </Button>
                             <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#31374D] rounded-none h-14 px-8 uppercase tracking-widest font-sans font-bold text-xs">
                                 Book a Visit
                             </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* "From Our Headmaster" - Split Section */}
            <section className="bg-[#f4f4f4] py-24">
                <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div className="relative">
                        <div className="absolute top-[-20px] left-[-20px] w-32 h-32 border-l-4 border-t-4 border-[#31374D]/20 z-0"></div>
                        <img src="https://images.unsplash.com/photo-1557862921-37829c790f19?q=80&w=2071&auto=format&fit=crop" className="w-full shadow-lg relative z-10 grayscale hover:grayscale-0 transition-all duration-700" alt="Headmaster" />
                        <div className="absolute bottom-[-20px] right-[-20px] w-32 h-32 border-r-4 border-b-4 border-[#31374D]/20 z-0"></div>
                    </div>
                    <div>
                        <h4 className="font-sans font-bold text-[#31374D]/60 uppercase tracking-widest text-sm mb-4">Welcome</h4>
                        <h2 className="text-4xl md:text-5xl mb-8 leading-tight">
                            FROM our <span className="italic font-light">HEADMASTER</span>
                        </h2>
                        <div className="w-20 h-1 bg-[#31374D] mb-8"></div>
                        <p className="text-lg md:text-xl font-light text-gray-600 leading-relaxed mb-8">
                            "We aim to allow every pupil to flourish and find their path. {schoolName} is not just a school; it is a community, a home, and a launchpad for future success."
                        </p>
                        <a href="#" className="inline-flex items-center gap-2 uppercase tracking-widest font-bold text-sm border-b border-[#31374D] pb-1 hover:text-[#31374D]/70 transition-colors">
                            Read Full Welcome <ArrowRight size={14} />
                        </a>
                    </div>
                </div>
            </section>

            {/* The Five Pillars - Text Grid */}
            <section className="py-24 bg-[#31374D] text-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                         <h2 className="text-4xl md:text-5xl mb-4">
                             OUR five <span className="italic font-light">PILLARS</span>
                         </h2>
                         <div className="w-px h-16 bg-white/30 mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-white/20 border-t border-b border-white/20">
                        {['Academic Excellence', 'Pastoral Care', 'Sporting Prowess', 'Creative Arts', 'Community Service'].map((pillar, i) => (
                            <div key={i} className="p-8 text-center group hover:bg-[#3d445e] transition-colors cursor-pointer">
                                <span className="block text-4xl mb-4 font-light opacity-30 group-hover:opacity-100 transition-opacity font-sans">0{i+1}</span>
                                <h3 className="text-lg font-bold uppercase tracking-widest leading-relaxed">{pillar}</h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* News Highlights - Clean Grid */}
            <section className="py-24 container mx-auto px-6">
                <div className="flex justify-between items-end mb-12 border-b border-gray-200 pb-6">
                     <h2 className="text-4xl">NEWS <span className="italic font-light text-gray-500">highlights</span></h2>
                     <a href="#" className="hidden md:block font-sans font-bold text-xs uppercase tracking-widest hover:underline">View Archive</a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        "https://images.unsplash.com/photo-1563222374-2dc41bb425c2?q=80&w=2070&auto=format&fit=crop",
                        "https://images.unsplash.com/photo-1543857770-724574c82b04?q=80&w=2070&auto=format&fit=crop",
                        "https://images.unsplash.com/photo-1564981797816-1043664bf78d?q=80&w=1974&auto=format&fit=crop"
                    ].map((img, i) => (
                        <div key={i} className="group cursor-pointer">
                             <div className="overflow-hidden mb-6 h-64">
                                 <img src={img} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" alt="News" />
                             </div>
                             <div className="text-xs font-sans font-bold text-[#31374D]/60 uppercase tracking-widest mb-2">October 12, 2026</div>
                             <h3 className="text-xl font-bold leading-tight group-hover:text-[#31374D]/70 transition-colors">
                                 {['Pupils Excel on National Stage', 'Outstanding Theatre Nominations', 'Summer Sports Tour Victory'][i]}
                             </h3>
                        </div>
                    ))}
                </div>
            </section>

             {/* Footer - Simple & Clean */}
             <footer className="bg-white border-t border-gray-100 py-16 text-center">
                 <div className="mb-8">
                     <div className="w-16 h-16 bg-[#31374D] text-white flex items-center justify-center font-bold text-2xl mx-auto mb-4">R</div>
                     <h2 className="text-2xl font-bold uppercase tracking-widest text-[#31374D]">{schoolName}</h2>
                 </div>
                 <div className="flex flex-wrap justify-center gap-8 text-xs font-sans font-bold uppercase tracking-widest text-gray-500 mb-8">
                     <a href="#" className="hover:text-[#31374D]">Careers</a>
                     <a href="#" className="hover:text-[#31374D]">Privacy Policy</a>
                     <a href="#" className="hover:text-[#31374D]">Terms</a>
                     <a href="#" className="hover:text-[#31374D]">Sitemap</a>
                     <a href="#" className="hover:text-[#31374D]">Contact</a>
                 </div>
                 <div className="text-gray-400 text-sm font-sans">
                     &copy; 2026 {schoolName}. All Rights Reserved.
                 </div>
             </footer>
        </div>
    );
};

export default ReptonHeritage;
